<pull-to-refresh (refresh)="onRefresh()">
    <div class="max-w-7xl flex flex-wrap mx-auto mt-8">
        @let myRoles = myRoles$ | async;

        <div class="w-full flex justify-between mb-4">
            <app-btn-back routeTo=".."></app-btn-back>
            @if (myRoles?.admin && group?.memberStatus === membershipStatus.active) {
            <div class="flex gap-2">
            <button type="button" (click)="onImportClick()"
                class="bg-white hover:bg-blueGray-50 text-sm text-blueGray-700 border border-blueGray-300 whitespace-nowrap w-fit flex items-center font-semibold px-4 py-2 rounded-lg outline-none focus:outline-none ease-linear transition-all duration-150">
                <i class="fas fa-file-import mr-2 leading-relaxed"></i>
                Import
            </button>
            <button type="button" (click)="onAddMemberClick()"
                class="bg-primary-500 hover:bg-primary-300 text-sm text-white disabled:bg-primary-200 whitespace-nowrap w-fit flex items-center font-semibold px-4 py-2 rounded-lg shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150">
                <i class="fa fa-add mr-2 leading-relaxed"></i>
                Add Member
            </button>
            </div>
            }
        </div>

        <h1 class="font-semibold text-2xl text-primary-500 mb-4">
            Members
        </h1>

        <div class="flex flex-row gap-4 w-full mb-4">
            @let groupStatsLoading = groupStatsLoading$ | async;
            <div
                class="w-full md:w-4/12 bg-white rounded shadow-lg px-4 py-2 flex flex-wrap justify-end content-between">
                <span class="w-full text-start text-sm text-blueGray-400">Total Active Members</span>
                @if (groupStatsLoading) {
                <div class="animate-pulse h-8 w-1/2 bg-slate-200 rounded flex justify-end"></div>
                } @else {
                <span class="w-full text-5xl text-right font-bold text-primary-500">{{group?.activeMembers ??
                    '-'}}</span>
                }
            </div>
            <div
                class="w-full md:w-4/12 bg-white rounded shadow-lg px-4 py-2 flex flex-wrap justify-end content-between">
                <span class="w-full text-start text-sm text-blueGray-400 block">New Members (30 days)</span>
                @if(memberStatsLoading) {
                <div class="animate-pulse h-8 w-1/2 bg-slate-200 rounded flex justify-end"></div>
                } @else {
                <span class="w-full text-5xl text-right font-bold text-primary-500">{{group?.newMembers ?? '-'}}</span>
                }
            </div>
            <div
                class="w-full md:w-4/12 bg-white rounded shadow-lg px-4 py-2 flex flex-wrap justify-end content-between">
                <span class="w-full text-start text-sm text-blueGray-400">Pending Request(s)</span>
                @if (groupStatsLoading) {
                <div class="animate-pulse h-8 w-1/2 bg-slate-200 rounded flex justify-end"></div>
                } @else {
                <span class="w-full text-5xl text-right font-bold text-primary-500">{{
                    group?.requestedMembers ?? '-'}}</span>
                }
            </div>
        </div>

        <div class="w-full my-4">
            <div class="w-full flex flex-col gap-4">
                @for (item of members$ | async; track item.id) {
                <div
                    class="bg-white shadow-lg rounded-lg w-full px-4 py-3 cursor-default flex flex-col items-start relative">
                    <!-- Flex row of member image/initials and name/joining date -->
                    <div class="flex flex-row justify-between items-start w-full gap-4">
                        <div class="flex flex-row items-center gap-4">
                            <!-- Left: Member Image or initials -->
                            <div class="flex-none">
                                @if(item.photoURL) {
                                <img [src]="item.photoURL"
                                    class="min-h-12 max-h-12 max-w-12 min-w-12 bg-white object-cover rounded-full border border-gray-200" />
                                } @else {
                                <div
                                    class="bg-primary-100 uppercase text-primary-500 text-base flex justify-center items-center text-center h-12 w-12 rounded-full font-bold">
                                    {{ item.name! | initials }}
                                </div>
                                }
                            </div>

                            <!-- Right of image: flex-col of name and joining date -->
                            <div class="flex flex-col">
                                <a [routerLink]="[item.id]"
                                    class="cursor-pointer font-bold text-sm text-blueGray-600 hover:text-primary-500 leading-snug">
                                    {{ item.name }}
                                </a>
                                <span class="text-xs text-blueGray-400 mt-0.5">
                                    Joined: {{ item.created.toDate() | moment: 'Do MMM YYYY' }}
                                </span>
                            </div>
                        </div>

                        <!-- Admin only actions at the right top corner -->
                        @if (myRoles?.admin && group?.memberStatus === membershipStatus.active) {
                        <div class="flex flex-row gap-2 whitespace-nowrap flex-none self-start">
                            <app-btn-fab (click)="onChangeStatus(item)" icon="fas fa-shuffle"
                                tooltip="Change Status"></app-btn-fab>
                        </div>
                        }
                    </div>

                    <!-- Below flex-row: member status chips -->
                    <div class="flex flex-row flex-wrap gap-2 mt-1 pl-16">
                        <app-chip [label]="item.status" [color]="chipColors[item.status]"></app-chip>
                        @if(item.memberType && item.memberType.indexOf(memberType.admin) > -1) {
                        <app-chip [label]="memberType.admin" [color]="chipColorAdmin"></app-chip>
                        }
                        @if(item.unregistered) {
                        <app-chip label="unregistered" [color]="chipColorLight"></app-chip>
                        }
                    </div>
                </div>
                }
                @if (loading$ | async) {
                <div
                    class="bg-white shadow-lg rounded-lg w-full px-4 py-3 flex flex-row items-center gap-4 animate-pulse">
                    <div class="h-12 w-12 bg-slate-200 rounded-full flex-none"></div>
                    <div class="flex-1 flex flex-col space-y-2">
                        <div class="h-4 bg-slate-200 rounded w-1/3"></div>
                        <div class="h-3 bg-slate-200 rounded w-1/4"></div>
                    </div>
                </div>
                }
            </div>
        </div>
        @if (members$ | async) {
        <div class="flex flex-row w-full justify-center px-4 mb-4">
            <button type="button" (click)="addMore()" [disabled]="loading$ | async"
                class="bg-white hover:bg-primary-100 active:bg-primary-500 active:text-white disabled:bg-primary-100 border-none shadow-lg text-xs rounded-full px-4 py-2">
                Show More
            </button>
        </div>
        }
    </div>
</pull-to-refresh>